import type { RangePickerValueType } from '@sede-enterprise/shell-ds-react-framework';
import {
  Badge,
  ButtonGroup,
  Icons,
  RangeDatePicker,
  Button as ShellButton,
  Sizes,
  Table,
  TextInput,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  TBody,
  TD as Td,
  TH as Th,
  THead,
  TR as Tr,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Table/components/TableElements/TableElements';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CET_TIME_ZONE, COMMON_MESSAGES, NUMBERS } from 'src/constants/Common';
import { GET_FLEX_DEALS, GET_FLEX_MESSAGE_BLOCKS } from 'src/constants/EUMarkets';
import { get, post } from 'src/services/GenesisApi';
import Button from 'src/UI/Button';
import Loader from 'src/UI/Loader';
import { getCurrentGasDay, handleError } from 'src/utils/common/utils';
import { Modal } from '../../SystemAdmin/NominationGroups/style';
import { FLEX_DEALS_BATCH_SIZE } from './constants';
import { FlexDealsContainer, FlexModalGlobalStyle } from './style';
import { IFlexBlockItem, IFlexDeal, IFlexDealsRequest, IFlexMessageBlock } from './types';

type DealView = 'flat' | 'flex';

const { THOUSAND, MILLION, THERMS_KWH_CONVERSION, TJ_CONVERSION } = NUMBERS;

const {
  ArrowCw,
  ArrowDownSquare,
  ArrowUpSquare,
  ChevronUpDown,
  Filter,
  Cross,
  TrashAltSolid,
  CheckCircleSolid,
  EyeSolid,
} = Icons;

dayjs.extend(utc);
dayjs.extend(timezone);

// Convert a GasVolume value from its API unit into KWh
const convertToKWh = (value: number, fromUnit: string): number => {
  switch (fromUnit) {
    case 'MWh':
      return value * THOUSAND;
    case 'GWh':
      return value * MILLION;
    case 'Therms':
      return value * THERMS_KWH_CONVERSION;
    case 'TJ':
      return value / TJ_CONVERSION;
    case 'KWh':
    default:
      return value;
  }
};

const DATE_YYYY_MM_DD = 'YYYY-MM-DD';
const ALL = 'all';

const formatDateTime = (iso: string): string => {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD MMM YYYY HH:mm') : iso;
};

const formatEpochToCET = (epoch?: number | null): string => {
  if (epoch == null) {
    return '';
  }
  return dayjs(epoch).tz(CET_TIME_ZONE).format('DD MMM YYYY HH:mm');
};

const getPeriodFromEpoch = (row: IFlexDeal): number | null =>
  row.periodFromEpoch ?? row.periodBegin ?? null;

const getPeriodToEpoch = (row: IFlexDeal): number | null =>
  row.periodToEpoch ?? row.periodEnd ?? null;

type SortDir = 'asc' | 'desc' | null;
type FlexDealRefFilter = 'dealId' | 'shellEntity';
type FlexLocationFilter = 'deliveryPoint' | 'counterparty';
type FlexFilterColumn = FlexDealRefFilter | FlexLocationFilter;

interface SortIconProps {
  col: keyof IFlexDeal;
  sortColumn: keyof IFlexDeal | null;
  sortDir: SortDir;
}

const SortIcon = ({ col, sortColumn, sortDir }: SortIconProps) => {
  if (sortColumn !== col || !sortDir) {
    return <ChevronUpDown className='sort-icon' />;
  }
  return sortDir === 'asc' ? (
    <ArrowUpSquare className='sort-icon' />
  ) : (
    <ArrowDownSquare className='sort-icon' />
  );
};

const fetchFlexDeals = (req: IFlexDealsRequest): Promise<IFlexDeal[]> =>
  post(GET_FLEX_DEALS, req as unknown as Record<string, unknown>).then((r) => r.data ?? []);

const fetchFlexBlocks = (messageId: number): Promise<IFlexMessageBlock[]> =>
  get(`${GET_FLEX_MESSAGE_BLOCKS}?blockId=${messageId}`).then((r) => r.data ?? []);

const FlexDeals = ({
  activeView,
  onViewChange,
}: {
  activeView: DealView;
  onViewChange: (view: DealView) => void;
}) => {
  const [visibleRows, setVisibleRows] = useState<IFlexDeal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);

  const [periodFrom, setPeriodFrom] = useState<string>(() =>
    getCurrentGasDay().format(DATE_YYYY_MM_DD),
  );
  const [periodTo, setPeriodTo] = useState<string>(() =>
    getCurrentGasDay().format(DATE_YYYY_MM_DD),
  );

  // Column filters (client-side)
  const [filterDealId, setFilterDealId] = useState('');
  const [filterShellEntity, setFilterShellEntity] = useState('');
  const [filterDeliveryPoint, setFilterDeliveryPoint] = useState('');
  const [filterCounterparty, setFilterCounterparty] = useState('');
  const [draftDealId, setDraftDealId] = useState('');
  const [draftShellEntity, setDraftShellEntity] = useState('');
  const [draftDeliveryPoint, setDraftDeliveryPoint] = useState('');
  const [draftCounterparty, setDraftCounterparty] = useState('');
  const [filterPopoverCol, setFilterPopoverCol] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const filterBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Sorting
  const [sortColumn, setSortColumn] = useState<keyof IFlexDeal | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  // View popup
  const [selectedRow, setSelectedRow] = useState<IFlexDeal | null>(null);
  const [blockData, setBlockData] = useState<IFlexMessageBlock[]>([]);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);

  const hasMoreRef = useRef(true);
  const nextPageRef = useRef(1);
  const isFetchingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const filterKey = `${periodFrom}|${periodTo}|${filterDealId}|${filterShellEntity}|${filterDeliveryPoint}|${filterCounterparty}|${sortColumn ?? ''}|${sortDir ?? ''}|${refreshCount}`;

  // Close column filter popup on outside click
  useEffect(() => {
    const handleDocMouseDown = (e: MouseEvent) => {
      if ((e.target as Element).closest('.col-filter-popup-flex')) {
        return;
      }
      setFilterPopoverCol(null);
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, []);

  const displayRows = useMemo(() => visibleRows, [visibleRows]);

  const loadMoreRows = useCallback(() => {
    if (isFetchingRef.current || !hasMoreRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setIsLoading(true);
    setHasError(false);

    const req: IFlexDealsRequest = {
      startDate: periodFrom,
      endDate: periodTo,
      referenceId: filterDealId.trim() || ALL,
      counterparty: filterCounterparty.trim() || ALL,
      deliveryPoint: filterDeliveryPoint.trim() || ALL,
      shellEntity: filterShellEntity.trim() || ALL,
      pageNumber: nextPageRef.current,
      pageSize: FLEX_DEALS_BATCH_SIZE,
      sortBy: sortColumn ?? 'receiptTime',
      sortOrder: sortDir ?? 'desc',
    };

    fetchFlexDeals(req)
      .then((rows) => {
        setVisibleRows((prev) => [...prev, ...rows]);
        if (rows.length > 0) {
          setTotalCount(rows[0].totalCount);
        }
        nextPageRef.current += 1;
        const loadedSoFar = (nextPageRef.current - 1) * FLEX_DEALS_BATCH_SIZE;
        const knownTotal = rows[0]?.totalCount ?? 0;
        hasMoreRef.current = rows.length === FLEX_DEALS_BATCH_SIZE && loadedSoFar < knownTotal;
        setIsLoading(false);
        isFetchingRef.current = false;
      })
      .catch((err) => {
        handleError(err, COMMON_MESSAGES.ERROR);
        setHasError(true);
        setIsLoading(false);
        isFetchingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Reset on filter change
  useEffect(() => {
    nextPageRef.current = 1;
    isFetchingRef.current = false;
    hasMoreRef.current = true;
    setVisibleRows([]);
    setTotalCount(null);
    setHasError(false);
    setIsLoading(false);
  }, [filterKey]);

  useEffect(() => {
    loadMoreRows();
  }, [loadMoreRows]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinelNode = sentinelRef.current;
    if (!sentinelNode) {
      return () => {};
    }
    let scrollParent: Element | null = null;
    let node: HTMLElement | null = sentinelNode.parentElement;
    while (node) {
      const { overflow, overflowY } = globalThis.getComputedStyle(node);
      if (/auto|scroll/.test(overflow + overflowY)) {
        scrollParent = node;
        break;
      }
      node = node.parentElement;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreRows();
        }
      },
      { root: scrollParent, rootMargin: '300px', threshold: 0 },
    );
    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [loadMoreRows]);

  const handleSortCol = (col: keyof IFlexDeal) => {
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    if (sortDir === 'desc') {
      setSortDir(null);
      setSortColumn(null);
      return;
    }
    setSortDir('asc');
  };

  const handleViewClick = (row: IFlexDeal) => {
    setSelectedRow(row);
    setBlockData([]);
    setIsLoadingBlocks(true);
    fetchFlexBlocks(row.blockId)
      .then((data) => {
        setBlockData(data);
        setIsLoadingBlocks(false);
      })
      .catch((err) => {
        handleError(err, 'Failed to load flex deal blocks.');
        setIsLoadingBlocks(false);
      });
  };

  const handleCloseModal = () => {
    setSelectedRow(null);
    setBlockData([]);
  };

  const applyColumnFilter = (col: FlexFilterColumn) => {
    if (col === 'dealId') {
      setFilterDealId(draftDealId.trim());
    }
    if (col === 'shellEntity') {
      setFilterShellEntity(draftShellEntity.trim());
    }
    if (col === 'deliveryPoint') {
      setFilterDeliveryPoint(draftDeliveryPoint.trim());
    }
    if (col === 'counterparty') {
      setFilterCounterparty(draftCounterparty.trim());
    }
    setFilterPopoverCol(null);
  };

  const clearAppliedFilter = (col: FlexFilterColumn) => {
    if (col === 'dealId') {
      setFilterDealId('');
      setDraftDealId('');
    }
    if (col === 'shellEntity') {
      setFilterShellEntity('');
      setDraftShellEntity('');
    }
    if (col === 'deliveryPoint') {
      setFilterDeliveryPoint('');
      setDraftDeliveryPoint('');
    }
    if (col === 'counterparty') {
      setFilterCounterparty('');
      setDraftCounterparty('');
    }
  };

  const clearAllAppliedFilters = () => {
    setFilterDealId('');
    setFilterShellEntity('');
    setFilterDeliveryPoint('');
    setFilterCounterparty('');
    setDraftDealId('');
    setDraftShellEntity('');
    setDraftDeliveryPoint('');
    setDraftCounterparty('');
  };

  const appliedFilters = useMemo(
    () =>
      [
        { key: 'dealId', label: 'Deal Id', value: filterDealId },
        { key: 'shellEntity', label: 'Shell Entity', value: filterShellEntity },
        {
          key: 'deliveryPoint',
          label: 'Delivery Point',
          value: filterDeliveryPoint,
        },
        {
          key: 'counterparty',
          label: 'Counterparty',
          value: filterCounterparty,
        },
      ].filter((f) => f.value.trim()),
    [filterDealId, filterShellEntity, filterDeliveryPoint, filterCounterparty],
  );

  // Parse all blocks from blockContentJson arrays, carrying the parent unit for conversion
  const parsedBlocks = useMemo((): (IFlexBlockItem & { unit: string })[] => {
    if (!blockData.length) {
      return [];
    }
    return blockData.flatMap((msg) => {
      try {
        const items = JSON.parse(msg.blockContentJson) as IFlexBlockItem[];
        return items.map((item) => ({ ...item, unit: msg.unit }));
      } catch {
        return [];
      }
    });
  }, [blockData]);

  return (
    <FlexDealsContainer>
      <FlexModalGlobalStyle />
      {/* Filters */}
      <div className='filters-card'>
        <div className='filters-row'>
          <div className='filter-field'>
            <span className='filter-label'>Date range</span>
            <RangeDatePicker
              format='DD MMM YYYY'
              allowClear={false}
              value={[dayjs(periodFrom), dayjs(periodTo)]}
              onChange={(val: RangePickerValueType) => {
                if (!val) {
                  return;
                }
                const [from, to] = val;
                if (from) {
                  setPeriodFrom(dayjs(from).format(DATE_YYYY_MM_DD));
                }
                if (to) {
                  setPeriodTo(dayjs(to).format(DATE_YYYY_MM_DD));
                }
              }}
              size='small'
              data-testid='flex-deals-date-range'
            />
          </div>
          {/* View toggle: Flat/Flex */}
          <div className='filters-view-toggle'>
            <ButtonGroup
              type='radio'
              size={Sizes.Small}
              selectedKey={activeView}
              items={[
                { value: 'flat', label: 'Flat' },
                { value: 'flex', label: 'Flex' },
              ]}
              onChange={(val) => onViewChange(val as DealView)}
              data-testid='inbox-view-toggle'
            />
          </div>
          <div className='filters-refresh'>
            <Button
              size='small'
              iconOnly
              icon={<ArrowCw />}
              onClick={() => setRefreshCount((c) => c + 1)}
              disabled={isLoading}
              data-testid='flex-deals-refresh'
              title='Refresh'
            />
          </div>
        </div>
      </div>

      {appliedFilters.length > 0 && (
        <div className='selected-filters-bar' data-testid='flex-deals-selected-filters'>
          {appliedFilters.map((filter) => (
            <div
              key={filter.key}
              className='filter'
              data-testid={`flex-deals-applied-filter-${filter.key}`}
            >
              <div className='display-label'>{filter.label}:</div>
              <Badge size={Sizes.ExtraSmall} className='display-value'>
                {filter.value}
                <ShellButton
                  className='badge-button'
                  size={Sizes.ExtraSmall}
                  variant='transparent'
                  icon={<Cross />}
                  onClick={() => clearAppliedFilter(filter.key as FlexFilterColumn)}
                  aria-label={`Clear ${filter.label} filter`}
                />
              </Badge>
            </div>
          ))}

          <div className='filter'>
            <ShellButton
              size={Sizes.ExtraSmall}
              icon={<TrashAltSolid />}
              variant='transparent'
              onClick={clearAllAppliedFilters}
              data-testid='flex-deals-clear-all-filters'
            >
              Clear
            </ShellButton>
          </div>
        </div>
      )}

      {/* Table */}
      <div className='table-wrapper'>
        <div
          className={`table-container${isLoading ? ' table-loading' : ''}`}
          data-testid='flex-deals-scroll-area'
        >
          <Table className='shell-table' size='small'>
            <THead>
              <Tr className='table-row'>
                {/* Import Status */}
                <Th className='table-column' style={{ textAlign: 'center' }}>
                  Status
                </Th>

                {/* Deal Id — sort + column filter */}
                <Th className='table-column'>
                  <div className='th-cell'>
                    <button
                      className='th-sort-trigger'
                      onClick={() => handleSortCol('referenceId')}
                    >
                      Deal Id
                      <SortIcon col='referenceId' sortColumn={sortColumn} sortDir={sortDir} />
                    </button>
                    <div className='th-filter-wrapper'>
                      <button
                        ref={(el) => {
                          filterBtnRefs.current['dealId'] = el;
                        }}
                        className={`th-filter-btn${filterPopoverCol === 'dealId' || filterDealId ? ' active' : ''}`}
                        data-testid='flex-deals-filter-btn-dealId'
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (filterPopoverCol === 'dealId') {
                            setFilterPopoverCol(null);
                          } else {
                            setDraftDealId(filterDealId);
                            const rect = filterBtnRefs.current['dealId']?.getBoundingClientRect();
                            if (rect) {
                              setPopoverPos({
                                top: rect.bottom + 4,
                                left: rect.left + rect.width / 2,
                              });
                            }
                            setFilterPopoverCol('dealId');
                          }
                        }}
                      >
                        <Filter width={12} height={12} />
                      </button>
                    </div>
                  </div>
                </Th>

                {/* Import Time */}
                <Th className='table-column th-sortable'>
                  <button className='th-sort-trigger' onClick={() => handleSortCol('receiptTime')}>
                    Import Time
                    <SortIcon col='receiptTime' sortColumn={sortColumn} sortDir={sortDir} />
                  </button>
                </Th>

                {/* Period From */}
                <Th className='table-column th-sortable'>
                  <button
                    className='th-sort-trigger'
                    onClick={() => handleSortCol('periodFromEpoch')}
                  >
                    Period From
                    <SortIcon col='periodFromEpoch' sortColumn={sortColumn} sortDir={sortDir} />
                  </button>
                </Th>

                {/* Period To */}
                <Th className='table-column th-sortable'>
                  <button
                    className='th-sort-trigger'
                    onClick={() => handleSortCol('periodToEpoch')}
                  >
                    Period To
                    <SortIcon col='periodToEpoch' sortColumn={sortColumn} sortDir={sortDir} />
                  </button>
                </Th>

                {/* Shell Entity — sort + column filter */}
                <Th className='table-column'>
                  <div className='th-cell'>
                    <button
                      className='th-sort-trigger'
                      onClick={() => handleSortCol('shellEntity')}
                    >
                      Shell Entity
                      <SortIcon col='shellEntity' sortColumn={sortColumn} sortDir={sortDir} />
                    </button>
                    <div className='th-filter-wrapper'>
                      <button
                        ref={(el) => {
                          filterBtnRefs.current['shellEntity'] = el;
                        }}
                        className={`th-filter-btn${filterPopoverCol === 'shellEntity' || filterShellEntity ? ' active' : ''}`}
                        data-testid='flex-deals-filter-btn-shellEntity'
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (filterPopoverCol === 'shellEntity') {
                            setFilterPopoverCol(null);
                          } else {
                            setDraftShellEntity(filterShellEntity);
                            const rect =
                              filterBtnRefs.current['shellEntity']?.getBoundingClientRect();
                            if (rect) {
                              setPopoverPos({
                                top: rect.bottom + 4,
                                left: rect.left + rect.width / 2,
                              });
                            }
                            setFilterPopoverCol('shellEntity');
                          }
                        }}
                      >
                        <Filter width={12} height={12} />
                      </button>
                    </div>
                  </div>
                </Th>

                {/* Counterparty — sort + column filter */}
                <Th className='table-column'>
                  <div className='th-cell'>
                    <button
                      className='th-sort-trigger'
                      onClick={() => handleSortCol('counterparty')}
                    >
                      Counterparty
                      <SortIcon col='counterparty' sortColumn={sortColumn} sortDir={sortDir} />
                    </button>
                    <div className='th-filter-wrapper'>
                      <button
                        ref={(el) => {
                          filterBtnRefs.current['counterparty'] = el;
                        }}
                        className={`th-filter-btn${filterPopoverCol === 'counterparty' || filterCounterparty ? ' active' : ''}`}
                        data-testid='flex-deals-filter-btn-counterparty'
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (filterPopoverCol === 'counterparty') {
                            setFilterPopoverCol(null);
                          } else {
                            setDraftCounterparty(filterCounterparty);
                            const rect =
                              filterBtnRefs.current['counterparty']?.getBoundingClientRect();
                            if (rect) {
                              setPopoverPos({
                                top: rect.bottom + 4,
                                left: rect.left + rect.width / 2,
                              });
                            }
                            setFilterPopoverCol('counterparty');
                          }
                        }}
                      >
                        <Filter width={12} height={12} />
                      </button>
                    </div>
                  </div>
                </Th>

                {/* Delivery Point — sort + column filter */}
                <Th className='table-column'>
                  <div className='th-cell'>
                    <button
                      className='th-sort-trigger'
                      onClick={() => handleSortCol('deliveryPoint')}
                    >
                      Delivery Point
                      <SortIcon col='deliveryPoint' sortColumn={sortColumn} sortDir={sortDir} />
                    </button>
                    <div className='th-filter-wrapper'>
                      <button
                        ref={(el) => {
                          filterBtnRefs.current['deliveryPoint'] = el;
                        }}
                        className={`th-filter-btn${filterPopoverCol === 'deliveryPoint' || filterDeliveryPoint ? ' active' : ''}`}
                        data-testid='flex-deals-filter-btn-deliveryPoint'
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (filterPopoverCol === 'deliveryPoint') {
                            setFilterPopoverCol(null);
                          } else {
                            setDraftDeliveryPoint(filterDeliveryPoint);
                            const rect =
                              filterBtnRefs.current['deliveryPoint']?.getBoundingClientRect();
                            if (rect) {
                              setPopoverPos({
                                top: rect.bottom + 4,
                                left: rect.left + rect.width / 2,
                              });
                            }
                            setFilterPopoverCol('deliveryPoint');
                          }
                        }}
                      >
                        <Filter width={12} height={12} />
                      </button>
                    </div>
                  </div>
                </Th>

                {/* View */}
                <Th className='table-column' style={{ textAlign: 'center' }}>
                  Quantity
                </Th>
              </Tr>
            </THead>
            <TBody>
              {displayRows.map((row) => (
                <Tr key={row.msgId} className='table-row'>
                  <Td className='table-column' style={{ textAlign: 'center' }}>
                    <CheckCircleSolid
                      width={18}
                      height={18}
                      style={{ color: '#22a06b', display: 'inline-block' }}
                    />
                  </Td>
                  <Td className='table-column'>{row.referenceId}</Td>
                  <Td className='table-column'>{formatDateTime(row.receiptTime)}</Td>
                  <Td className='table-column'>{formatEpochToCET(getPeriodFromEpoch(row))}</Td>
                  <Td className='table-column'>{formatEpochToCET(getPeriodToEpoch(row))}</Td>
                  <Td className='table-column'>{row.shellEntity}</Td>
                  <Td className='table-column'>{row.counterparty}</Td>
                  <Td className='table-column'>{row.deliveryPoint}</Td>
                  <Td className='table-column' style={{ textAlign: 'center' }}>
                    <Button
                      variant='transparent'
                      title={'View Deal Quanity'}
                      size='small'
                      key={`flex-view-btn-${row.blockId}`}
                      onClick={() => handleViewClick(row)}
                      data-testid={`flex-view-${row.blockId}`}
                      icon={<EyeSolid />}
                      iconOnly
                    />
                  </Td>
                </Tr>
              ))}
              {displayRows.length === 0 && !isLoading && (
                <Tr className='table-row'>
                  <Td colSpan={9} className='table-column empty-cell'>
                    {hasError
                      ? 'Failed to load data. Please try again.'
                      : 'No records match the selected filters'}
                  </Td>
                </Tr>
              )}
            </TBody>
          </Table>
          <div ref={sentinelRef} className='load-sentinel' />
        </div>

        {isLoading && (
          <div className='loading-more' data-testid='flex-deals-loading'>
            <Loader />
          </div>
        )}
      </div>
      {/* Row count footer */}
      {visibleRows.length > 0 && (
        <div className='row-count'>
          <span className='row-count-loaded'>
            {displayRows.length.toLocaleString('en-GB')} loaded
          </span>
          {totalCount !== null && (
            <>
              <span className='row-count-sep'>·</span>
              <span className='row-count-total'>
                {totalCount.toLocaleString('en-GB')} Total Records
              </span>
            </>
          )}
        </div>
      )}
      {/* Column filter popups — portals escape the thead stacking context */}
      {filterPopoverCol === 'dealId' &&
        createPortal(
          <div
            className='col-filter-popup-flex'
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className='col-filter-input-row-flex'>
              <TextInput
                size={Sizes.Small}
                placeholder='Filter by deal id'
                value={draftDealId}
                onChange={(e) => setDraftDealId(e.target.value)}
                data-testid='col-filter-deal-id-flex'
                autoFocus
              />
              <Button size='small' onClick={() => applyColumnFilter('dealId')}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
      {filterPopoverCol === 'shellEntity' &&
        createPortal(
          <div
            className='col-filter-popup-flex'
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className='col-filter-input-row-flex'>
              <TextInput
                size={Sizes.Small}
                placeholder='Filter by shell entity'
                value={draftShellEntity}
                onChange={(e) => setDraftShellEntity(e.target.value)}
                data-testid='col-filter-shell-entity-flex'
                autoFocus
              />
              <Button size='small' onClick={() => applyColumnFilter('shellEntity')}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
      {filterPopoverCol === 'counterparty' &&
        createPortal(
          <div
            className='col-filter-popup-flex col-filter-popup-flex-wide'
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className='col-filter-input-row-flex'>
              <TextInput
                size={Sizes.Small}
                placeholder='Filter by counterparty'
                value={draftCounterparty}
                onChange={(e) => setDraftCounterparty(e.target.value)}
                data-testid='col-filter-counterparty-flex'
                autoFocus
              />
              <Button size='small' onClick={() => applyColumnFilter('counterparty')}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
      {filterPopoverCol === 'deliveryPoint' &&
        createPortal(
          <div
            className='col-filter-popup-flex'
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className='col-filter-input-row-flex'>
              <TextInput
                size={Sizes.Small}
                placeholder='Filter by delivery point'
                value={draftDeliveryPoint}
                onChange={(e) => setDraftDeliveryPoint(e.target.value)}
                data-testid='col-filter-delivery-point-flex'
                autoFocus
              />
              <Button size='small' onClick={() => applyColumnFilter('deliveryPoint')}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
      {selectedRow && (
        <Modal
          className='deal-quantity-modal'
          title='Flex Deal Quantity'
          open
          onClose={handleCloseModal}
          maskClosable={false}
          isDraggable
          width={600}
          footer={
            <div className='button-style'>
              <Button variant='outlined' onClick={handleCloseModal}>
                Close
              </Button>
            </div>
          }
        >
          <div className='flex-modal-body'>
            <div className='deal-id'>
              <strong>Deal Id:</strong> {selectedRow.referenceId}
            </div>
            {isLoadingBlocks ? (
              <div className='blocks-loading'>
                <Loader />
              </div>
            ) : (
              <div className='blocks-table-scroll'>
                <Table className='blocks-table' size='small'>
                  <THead>
                    <Tr>
                      <Th>Period From</Th>
                      <Th>Period To</Th>
                      <Th>Quantity (KWh)</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {parsedBlocks.map((block) => (
                      <Tr key={`${block.PeriodFrom}-${block.PeriodTo}`}>
                        <Td>{formatEpochToCET(block.PeriodFrom)}</Td>
                        <Td>{formatEpochToCET(block.PeriodTo)}</Td>
                        <Td
                          style={{
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {convertToKWh(block.GasVolume, block.unit).toLocaleString('en-GB', {
                            maximumFractionDigits: 3,
                          })}
                        </Td>
                      </Tr>
                    ))}
                    {parsedBlocks.length === 0 && (
                      <Tr>
                        <Td colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>
                          No blocks found
                        </Td>
                      </Tr>
                    )}
                  </TBody>
                </Table>
              </div>
            )}
          </div>
        </Modal>
      )}
    </FlexDealsContainer>
  );
};

export default FlexDeals;
